<template>
  <StepLayout
    :back="back"
    :next="next"
    :isCreateRoute="isCreateRoute"
    :isEditStepRoute="isEditStepRoute"
    buttonDataEc="envelopes_documents_page"
    helpscoutArticle="66588d2fd5f6cf2baa1eb9d9"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.documents.title') }}
    </template>

    <div
      ref="screen"
      class="relative"
    >
      <div class="flex flex-col max-w-screen-sm mb-md">
        <FloatingLabelInput
          v-model.trim="titleRef"
          aria-label="title"
          :placeholder="$t('ui.envelopes.create.steps.documents.placeholderOptional')"
          :hasError="hasErrors('title')"
          data-ec="envelopes_documents_page"
          data-et="name_input"
          @input="validate('title')"
        />
        <ValidationError
          :hasError="hasErrors('title')"
          :errorMessage="getError('title')"
        />
      </div>

      <FilePicker
        class="mb-lg"
        uploadClasses="min-h-250 h-1"
        acceptedFileTypes=".pdf,.docx,.doc,.xlsx,.xls"
        acceptedFileTypesString="PDF, Word, Excel"
        :uploaderMessage="$t('ui.envelopes.create.steps.documents.uploaderMessage')"
        @add="addDocuments"
      >
        <div class="flex flex-col gap-sm">
          <span>{{ $t('ui.envelopes.create.steps.documents.or') }}</span>

          <div class="flex-none">
            <BaseButton
              @click.native="templateSelectorOverlay && templateSelectorOverlay.open()"
              variant="tertiary"
              size="small"
              data-ec="envelopes_documents_page"
              data-et="select_templates_page_button"
            >
              {{ $t('functional.selectTemplates') }}
            </BaseButton>
          </div>
        </div>

      </FilePicker>

      <ValidationError
        :hasError="hasErrors('documents')"
        :errorMessage="getError('documents')"
        class="mt-lg"
      />

    </div>

    <OrderableList
      :value="documentsList"
      @input="updateDocumentsList"
      ref="orderableList"
    >
      <template v-slot:default="{ item }">
        <DismissibleInfoPill
          :text="item.name"
          :id="item.id"
          @dismiss="remove"
          ec="documents_screen"
        />
      </template>
    </OrderableList>

    <TemplateSelectorOverlay
      ref="templateSelectorOverlay"
      :selectedTemplates="templates"
      @addTemplates="addTemplates"
      @removeTemplates="removeTemplates"
    />
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, nextTick, ref, watch } from 'vue';
import { useReactiveMap, useReactiveSet } from 'vue-reactive-collection';
import { useRoute } from 'vue-router/composables';

import simpleHash from '@/utils/simpleHash';
import {
  useCreateUpdateEnvelope,
  useEnvelopesRoutes,
  useEnvelopesStepsNavigation,
  useProgressUpdate,
} from '@/composables/envelopes';
import {
  useErrors,
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { useScrollParent } from '@/composables/utils/dom/useScrollParent';
import { EnvelopeDocumentsUpdate } from '@/store/modules/envelopes/models/Envelope';

import StepLayout from '@/components/layouts/StepLayout';
import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import FilePicker from '@/components/library/filePickers/FilePicker/FilePicker.vue';
import OrderableList from '@/components/library/lists/orderableList/OrderableList/OrderableList.vue';
import DismissibleInfoPill from '@/components/library/pills/DismissibleInfoPill/DismissibleInfoPill.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import TemplateSelectorOverlay from '@/components/screens/envelopeCreate/TemplateSelectorOverlay.vue';

/** @typedef {import('@/data/schema/documents').TUpdateDocument} TUpdateDocument */
/** @typedef {import('@/data/schema/documents').TUpdateDocuments} TUpdateDocuments */
/** @typedef {import('@/data/schema/documents').TCurrentDocument} TCurrentDocument */
/** @typedef {import('@/data/schema/documents').TCurrentDocuments} TCurrentDocuments */
/** @typedef {import('@/components/screens/envelopeCreate/TemplateSelectorScreen.vue').TTemplate} TTemplate */

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  /** @type {import('vue').PropOptions<TCurrentDocuments>} */
  documents: {
    required: false,
  },
});

/** @type {import('vue').Ref<InstanceType<typeof TemplateSelectorOverlay>|null>} */
const templateSelectorOverlay = ref(null);

/** @type {import('vue').Ref<HTMLElement|undefined>} */
const screen = ref();

const titleRef = ref(props.title || '');

/**
 * Map of titles by fingerprint.
 * @type {import('vue').Ref<Map<string, string>>}
 */
const addedTemplates = useReactiveMap();
/**
 * Map of files and titles by fingerprint.
 * @type {import('vue').Ref<Map<string, {file: File, title: string}>>}
 */
const addedFiles = useReactiveMap();
/**
 * Set of fingerprints of documents to remove.
 * @type {import('vue').Ref<Set<string>>}
 */
const removedDocuments = useReactiveSet();

/** @type {import('vue').Ref<Set<string>>} */
const documentsOrder = useReactiveSet(props.documents?.map(doc => doc.fingerprint));

/**
 * Matches the document with the provided fingerprint.
 * @param {string} fingerprint - The fingerprint to match.
 */
function matchFingerprint(fingerprint) {
  /** @param {TCurrentDocument} document */
  return document =>
    'fingerprint' in document && document.fingerprint === fingerprint;
}

/**
 * Adds templates with the specified fingerprint and title.
 *
 * @param {Array<TTemplate>|Map<TTemplate>} templates - The templates to be added.
 */
function addTemplates(templates) {
  templates.forEach(({ fingerprint, title }) => {
    if (!removedDocuments.value.delete(fingerprint)) {
      addedTemplates.value.set(fingerprint, title);
    }

    documentsOrder.value.add(fingerprint);
  });
}

/**
 * Adds the specified files to the documents.
 *
 * @param {FileList} files - The files to be added.
 */
function addDocuments(files) {
  Array.from(files).forEach((file) => {
    const fingerprint = `file-${simpleHash(file.name + file.size + file.lastModified)}`;
    addedFiles.value.set(fingerprint, {
      title: file.name,
      file,
    });

    documentsOrder.value.add(fingerprint);
  });
}

/**
 * Removes an added file, template or document with the specified fingerprint.
 *
 * @param {string} fingerprint - The fingerprint of the file, template or document to be removed.
 */
function remove(fingerprint) {
  const removed =
    addedTemplates.value.delete(fingerprint) ||
    addedFiles.value.delete(fingerprint);

  if (!removed) {
    removedDocuments.value.add(fingerprint);
  }

  documentsOrder.value.delete(fingerprint);
}

/**
 * Removes the specified templates.
 *
 * @param {Array<string>|Set<string>} fingerprints - The fingerprints of the templates to be removed.
 */
function removeTemplates(fingerprints) {
  fingerprints.forEach(remove);
}

/**
 * Checks if a document is a template.
 *
 * @param {TCurrentDocument|TUpdateDocument} document - The document to check.
 * @returns {document is TCurrentDocument} - Returns true if the document is a template, false otherwise.
 */
function isTemplate(document) {
  return 'is_template' in document && !!document.is_template;
}

/**
 * Checks that a document is not marked as removed.
 *
 * @param {Object} document - The document to check.
 * @returns {boolean} - Returns true if the document is not removed, false otherwise.
 */
function isNotRemoved(document) {
  return !removedDocuments.value.has(document.fingerprint);
}

const templates = computed(() => [
  ...(props.documents || [])
    .filter(isTemplate)
    .filter(isNotRemoved)
    .map(document => ({
      fingerprint: document.fingerprint,
      title: document.title,
    })),
  ...[...addedTemplates.value].map(([fingerprint, title]) => ({
    fingerprint,
    title,
  })),
]);

const documentsList = computed(() => [...documentsOrder.value]
  .map((fingerprint) => {
    const document = props.documents?.find(matchFingerprint(fingerprint));
    if (document) {
      return {
        id: document.fingerprint,
        name: document.title,
      };
    }

    const addedTemplate = addedTemplates.value.get(fingerprint);
    if (addedTemplate) {
      return {
        id: fingerprint,
        name: addedTemplate,
      };
    }

    const addedFile = addedFiles.value.get(fingerprint);
    if (addedFile) {
      return {
        id: fingerprint,
        name: addedFile.title,
      };
    }

    return undefined;
  })
  .filter(/** @returns {document is {id:string,name:string}} */ document => !!document));

/**
 * Updates the documents list with a new list of documents.
 *
 * @param {{id:string}[]} newList - The new list of documents to update the documents list with.
 */
function updateDocumentsList(newList) {
  documentsOrder.value = new Set(newList.map(item => item.id));
}

const updateDocuments = computed(() => [...documentsOrder.value]
  .map(/** @returns {TUpdateDocument|undefined} */ (fingerprint, index) => {
    const document = props.documents?.find(matchFingerprint(fingerprint));
    if (document) {
      return {
        document_fingerprint: document.fingerprint,
        order: index,
      };
    }
    const addedTemplate = addedTemplates.value.get(fingerprint);
    if (addedTemplate) {
      return {
        template_fingerprint: fingerprint,
        order: index,
      };
    }
    const addedFile = addedFiles.value.get(fingerprint);
    if (addedFile) {
      return {
        file: addedFile.file,
        order: index,
      };
    }

    return undefined;
  })
  .filter(/** @returns {document is TUpdateDocument} */ document => !!document));

const orderableList = ref();
const scrollContainer = useScrollParent(screen);
// Adjust scroll position when a document is added or removed
watch(documentsList, async (newList, oldList) => {
  if (newList.length === oldList.length) {
    return;
  }

  // Wait for the list in the DOM to be updated
  await nextTick();

  const lastListItem = orderableList.value.$el.lastElementChild;
  const lastListItemHeight = lastListItem.offsetHeight;

  const added = newList.length > oldList.length;

  scrollContainer.value.scrollTop += added
    ? lastListItemHeight
    : -lastListItemHeight;
});

const route = useRoute();
const { isCreateRoute, isEditStepRoute } = useEnvelopesRoutes();
const progressUpdate = useProgressUpdate();

const { updateEnvelope } = useCreateUpdateEnvelope(route.params.fingerprint);

const documentsUpdate = computed(() => ({
  title: titleRef.value || documentsList.value[0]?.name || '',
  documents: updateDocuments.value,
  steps_progress: progressUpdate.value,
}));

const {
  isValid, data, errors, validate,
} = useValidation(
  documentsUpdate,
  EnvelopeDocumentsUpdate,
);

const { hasErrors, getError } = useErrors(errors);

watch(documentsList, () => {
  if (errors.value?.documents) {
    validate('documents');
  }
});

const update = useUpdateWithValidation(updateEnvelope, data, validate);

const { back, next } = useEnvelopesStepsNavigation(update, isValid);
</script>
